<script setup lang="ts">
import type { ScopeNode } from "@/types/bbel-types";
import { sampleFromHint } from "./bbel-palette";

/**
 * Recursive scope-tree renderer for the BBEL palette (#5683). Clicking a leaf's label inserts that
 * node's full path; clicking a branch's label (or caret) expands/collapses it — branches insert via
 * the `+` that shows on row hover, so browsing never pastes a half-path by accident. While the
 * palette search is active it renders the pruned tree with `forceExpand` (every surviving branch
 * open) and `highlight` (the matched text marked). Self-references by filename for the recursion.
 */
const props = defineProps<{
  nodes: ScopeNode[];
  expanded: Set<string>;
  /** Open every branch regardless of the expanded set (search mode — the tree is pre-pruned). */
  forceExpand?: boolean;
  /** Search term to highlight inside labels (search mode). */
  highlight?: string;
}>();

const emit = defineEmits<{ insert: [path: string]; toggle: [path: string] }>();

/** Leaf rows show their sampled value inline (muted, right after the key) — the `type ·` prefix
 *  stays hover-only (the full hint is the row's tooltip). Descriptions without a sample show as-is. */
function sample(node: ScopeNode): string {
  return node.children?.length ? "" : sampleFromHint(node.hint);
}

/** Label split around the first highlight match; `mid` empty = nothing to mark. */
function labelParts(label: string): { pre: string; mid: string; post: string } {
  const q = props.highlight?.trim().toLowerCase() ?? "";
  const at = q ? label.toLowerCase().indexOf(q) : -1;
  if (at < 0) return { pre: label, mid: "", post: "" };
  return { pre: label.slice(0, at), mid: label.slice(at, at + q.length), post: label.slice(at + q.length) };
}
</script>

<template>
  <ul class="bbel-tree list-unstyled mb-0">
    <li v-for="node in nodes" :key="node.path">
      <div class="bbel-tree-row d-flex align-items-center">
        <button
          v-if="node.children?.length"
          type="button"
          class="bbel-tree-caret btn btn-sm p-0"
          @click.stop="emit('toggle', node.path)"
          @mousedown.prevent
        >
          <i
            class="bi small"
            :class="forceExpand || expanded.has(node.path) ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
        </button>
        <span v-else class="bbel-tree-caret"></span>
        <!-- Branch labels grow so the hover `+` stays on the right edge; leaf labels hug their
             text so the inline sample sits right next to the key. -->
        <button
          type="button"
          class="bbel-tree-label btn btn-sm text-start p-0"
          :class="{ 'flex-grow-1': node.children?.length, 'bbel-tree-locked': node.locked }"
          :title="node.hint"
          @click="node.locked ? undefined : node.children?.length ? emit('toggle', node.path) : emit('insert', node.path)"
          @mousedown.prevent
        >
          <template v-if="labelParts(node.label).mid"
            >{{ labelParts(node.label).pre }}<mark class="p-0">{{ labelParts(node.label).mid }}</mark
            >{{ labelParts(node.label).post }}</template
          >
          <template v-else>{{ node.label }}</template>
        </button>
        <i v-if="node.locked" class="bi bi-lock-fill bbel-tree-sample flex-shrink-0" :title="node.hint"></i>
        <span v-else-if="sample(node)" class="bbel-tree-sample text-truncate" :title="node.hint">
          {{ sample(node) }}
        </span>
        <button
          v-if="node.children?.length"
          type="button"
          class="bbel-tree-add btn btn-sm p-0"
          :title="`Insert ${node.path}`"
          @click.stop="emit('insert', node.path)"
          @mousedown.prevent
        >
          <i class="bi bi-plus-circle small"></i>
        </button>
      </div>
      <div
        v-if="node.children?.length && (forceExpand || expanded.has(node.path))"
        class="bbel-tree-children"
      >
        <BbelScopeTree
          :nodes="node.children"
          :expanded="expanded"
          :force-expand="forceExpand"
          :highlight="highlight"
          @insert="emit('insert', $event)"
          @toggle="emit('toggle', $event)"
        />
      </div>
    </li>
  </ul>
</template>

<!-- Shared look (rows, caret, label, sample) lives in scope-tree.css; the rules below are the
     palette's own: the hover-only insert `+` and the locked-secret member treatment. -->
<style scoped src="./scope-tree.css"></style>
<style scoped>
/* Locked (secret) members: grey text + the lock beside it — no border, no disabled chrome. */
.bbel-tree-label.bbel-tree-locked {
  color: var(--bs-secondary-color);
  cursor: default;
}
.bbel-tree-add {
  visibility: hidden;
  color: var(--bs-secondary-color);
  line-height: 1;
}
.bbel-tree-row:hover .bbel-tree-add {
  visibility: visible;
}
.bbel-tree-add:hover {
  color: var(--bs-primary);
}
</style>
